import { GenieBranding, SignatureLayout } from "@/types/ui/branding";

/**
 * Preview-only port of HG Mail's `renderGenieSignatureHtml`
 * (Help-Genie-Mail/lib/email/signature.ts) and the report-email "Email Signature"
 * block (supabase/functions/_shared/email/templates). All three read the exact same
 * `signatureStyle` fields off `branding` — including the phoneNumber/liveUrl
 * snapshot BrandingModal writes on save — so this preview shows exactly what every
 * renderer downstream will actually produce, with no sample/placeholder data.
 */

const INK = "#101828";
const MUTED = "#5B6779";
const BORDER = "#E3E8EF";
const UNBRANDED = "#344054";
const FONT =
  "'Poppins','Poppins Regular',-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Helvetica,Arial,sans-serif";
const AVATAR_TINT = 0.28;

interface PreviewChannel {
  label: string;
  value: string | null;
}

function hostLabel(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** Same channels HG Mail's signature shows, built from the same
 * signatureStyle.phoneNumber/liveUrl snapshot — so the preview matches exactly
 * what every renderer of this signature will actually draw. */
function buildChannels(genieName: string, phoneNumber: string | null, liveUrl: string | null): PreviewChannel[] {
  const channels: PreviewChannel[] = [];
  if (liveUrl) channels.push({ label: "Chat", value: `Talk to ${genieName}` });
  if (phoneNumber) channels.push({ label: "Call", value: phoneNumber });
  if (liveUrl) channels.push({ label: "Web", value: hostLabel(liveUrl) });
  return channels;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function safeBrandColor(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const short = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/.exec(trimmed);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed : null;
}

function tintToWhite(color: string, strength: number): string {
  return `#${[1, 3, 5]
    .map((index) => Number.parseInt(color.slice(index, index + 2), 16))
    .map((channel) => Math.round(channel * strength + 255 * (1 - strength)))
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

interface SignaturePreviewOptions {
  genieName: string;
  layout: SignatureLayout;
  showDescription: boolean;
  showMascot: boolean;
  showQrCode: boolean;
  showChannelBadges: boolean;
  descriptionText?: string | null;
  phoneNumber?: string | null;
  liveUrl?: string | null;
}

export function renderSignaturePreviewHtml(branding: GenieBranding, options: SignaturePreviewOptions): string {
  const genieName = options.genieName.trim() || "Your Genie";
  const brandColor = safeBrandColor(branding.primaryColor);
  const primary = brandColor ?? UNBRANDED;
  const secondary = safeBrandColor(branding.secondaryColor) ?? primary;
  const angle =
    typeof branding.gradientAngle === "number" && Number.isFinite(branding.gradientAngle)
      ? Math.max(0, Math.min(360, branding.gradientAngle))
      : 135;
  const useGradient = Boolean(branding.gradientEnabled) && Boolean(brandColor) && secondary !== primary;
  const gradient = useGradient ? `linear-gradient(${angle}deg,${primary},${secondary})` : "none";
  const logoUrl = branding.logoUrl || branding.customAvatarUrl || null;

  const avatarFill = tintToWhite(primary, AVATAR_TINT);
  const avatarGradient = useGradient
    ? `linear-gradient(${angle}deg,${avatarFill},${tintToWhite(secondary, AVATAR_TINT)})`
    : "none";
  const chipFill = tintToWhite(primary, 0.07);
  const chipBorder = tintToWhite(primary, 0.22);

  const description = options.showDescription
    ? options.descriptionText?.trim() || "Always-on help, whenever you need it."
    : "";

  const showArt = options.layout !== "compact";
  const centered = options.layout === "centered";

  const logo = logoUrl
    ? `<td width="74" valign="middle" style="padding:0 16px 0 0"><div style="width:58px;height:58px;border-radius:29px;background-color:${avatarFill};background-image:${avatarGradient};text-align:center;font-size:0;line-height:0"><img src="${escapeHtml(logoUrl)}" width="58" height="58" alt="${escapeHtml(genieName)}" style="display:block;width:58px;height:58px;border:0;border-radius:29px;object-fit:contain"></div></td>`
    : "";

  // Same Supabase Storage asset the send-time renderer uses
  // (supabase/functions/_shared/email/templates/_signature-block.ts) — not a
  // placeholder box, so this preview shows what will actually be sent.
  const mascotUrl = showArt && options.showMascot
    ? "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/email-assets/mascot-thumbsup.png"
    : null;
  const mascot = mascotUrl
    ? `<td width="12%" valign="middle" align="right" style="width:12%;box-sizing:border-box;padding:0 2px 0 16px;font-size:0;line-height:0"><img src="${escapeHtml(mascotUrl)}" width="86" height="86" alt="Help Genie" style="display:block;width:100%;max-width:86px;height:auto;border:0"></td>`
    : "";

  // Same public QR generator used by both the send-time renderer and this
  // app's other QR previews (api.qrserver.com) — no image-generating endpoint
  // of this app's own exists for this.
  const qrUrl =
    showArt && options.showQrCode && options.liveUrl
      ? `https://api.qrserver.com/v1/create-qr-code/?size=132x132&data=${encodeURIComponent(options.liveUrl)}`
      : null;
  const qr = qrUrl
    ? `<td width="10%" valign="middle" align="right" style="width:10%;box-sizing:border-box;padding-left:14px"><img src="${escapeHtml(qrUrl)}" width="66" height="66" alt="Scan to chat with ${escapeHtml(genieName)}" style="display:block;width:100%;max-width:66px;height:auto;border:1px solid ${BORDER};border-radius:10px"><div style="margin-top:5px;font-family:${FONT};color:${MUTED};font-size:9px;font-weight:500;line-height:12px;text-align:center;white-space:nowrap">Scan to chat</div></td>`
    : "";

  const channels = options.showChannelBadges
    ? buildChannels(genieName, options.phoneNumber || null, options.liveUrl || null)
    : [];
  const channelRow = channels.length
    ? channels.map((channel) => {
        const value = channel.value
          ? ` <span style="color:${MUTED};font-weight:500">${escapeHtml(channel.value)}</span>`
          : "";
        return `<span style="display:inline-block;max-width:100%;box-sizing:border-box;margin:0 8px 8px 0;padding:7px 14px;border-radius:999px;background-color:${chipFill};border:1px solid ${chipBorder};font-family:${FONT};color:${INK};font-size:12px;font-weight:600;line-height:16px;white-space:normal"><span style="color:${primary};font-weight:700">${escapeHtml(channel.label)}</span>${value}</span>`;
      }).join("")
    : "";

  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;border-collapse:separate;border-spacing:0;font-family:${FONT}"><tr><td height="4" bgcolor="${primary}" style="height:4px;background-color:${primary};background-image:${gradient};font-size:0;line-height:0;border-radius:16px 16px 0 0">&nbsp;</td></tr><tr><td style="padding:18px 20px;background-color:#FFFFFF;border:1px solid ${BORDER};border-top:0;border-radius:0 0 16px 16px"><table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="table-layout:auto"><tr>${centered ? "" : logo}<td valign="middle" align="${centered ? "center" : "left"}"><div style="font-family:${FONT};color:${INK};font-size:18px;font-weight:700;line-height:24px">${escapeHtml(genieName)}<span style="display:inline-block;margin-left:8px;padding:3px 9px;border-radius:999px;background-color:${tintToWhite(primary, 0.13)};font-family:${FONT};color:${primary};font-size:9px;font-weight:700;letter-spacing:.1em;line-height:12px;text-transform:uppercase;vertical-align:middle">Genie</span></div>${description ? `<div style="margin-top:3px;font-family:${FONT};color:${MUTED};font-size:12.5px;font-weight:400;line-height:18px">${escapeHtml(description)}</div>` : ""}</td>${centered ? "" : `${mascot}${qr}`}</tr></table>${channelRow ? `<div style="margin-top:15px;padding-top:13px;border-top:1px solid ${BORDER};text-align:${centered ? "center" : "left"}">${channelRow}</div>` : ""}<div style="margin-top:${channelRow ? "8" : "15"}px;font-family:${FONT};color:#98A2B3;font-size:10.5px;font-weight:400;line-height:15px;text-align:${centered ? "center" : "left"}"><span style="font-family:${FONT};color:${primary};font-weight:700">Powered by Help Genie</span> &middot; one Genie, every channel</div></td></tr></table>`;
}
